import { HttpErrorResponse } from '@angular/common/http';
import { MonoTypeOperatorFunction, fromEvent, race, retry, throwError, timer } from 'rxjs';

/** Razmak pre prvog ponovnog pokušaja; svaki sledeći je duplo duži, do RETRY_MAX_MS. */
const RETRY_FIRST_MS = 1000;
const RETRY_MAX_MS = 30000;

/**
 * Greška posle koje ima smisla poslati ISTI zahtev ponovo:
 * • status 0 — zahtev nije ni stigao do aplikacije (nema mreže, čvor iza gateway-a se baš gasi);
 * • 5xx — odgovorio je gateway ili server koji se diže (502/503/504; service worker na grešku
 *   mreže vraća izmišljen 504);
 * • 408 / 429 — zahtev je istekao ili je odbijen zbog opterećenja.
 *
 * Odgovor same aplikacije (`success: false`, 4xx) NIJE prolazna greška: ponavljanje ne menja
 * ishod (nema prava, istekla prijava…), samo bi zasipalo server.
 */
export function isTransientHttpError(error: unknown): error is HttpErrorResponse {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
}

/** Razmak pre `attempt`-og ponovnog pokušaja (1, 2, 4… s, najviše RETRY_MAX_MS). */
export function retryDelayMs(attempt: number): number {
  return Math.min(RETRY_FIRST_MS * 2 ** (attempt - 1), RETRY_MAX_MS);
}

/**
 * Ponavlja zahtev dok ne uspe, ali samo posle prolazne greške (v. `isTransientHttpError`) —
 * svaka druga greška ide dalje kao i bez ovog operatora.
 *
 * Za učitavanja koja se traže JEDNOM pa se na njih dugo oslanja (meni, podešavanja lokala): bez
 * ponavljanja jedan neuspeo zahtev ostavlja ekran bez podataka do sledećeg reloada strane.
 * Samo za idempotentne zahteve (GET) — pokušaja nema ograničeno, prekida ih tek unsubscribe.
 *
 * Kad se mreža vrati (`online`), sledeći pokušaj ide odmah, ne posle preostalog čekanja.
 *
 * @param onRetry poziva se pre svakog čekanja — za log; `attempt` broji ponovne pokušaje od 1.
 */
export function retryTransient<T>(
  onRetry?: (error: HttpErrorResponse, attempt: number, delayMs: number) => void
): MonoTypeOperatorFunction<T> {
  return retry({
    delay: (error, attempt) => {
      if (!isTransientHttpError(error)) {
        return throwError(() => error);
      }

      const delayMs = retryDelayMs(attempt);
      onRetry?.(error, attempt, delayMs);

      return race(timer(delayMs), fromEvent(window, 'online'));
    }
  });
}

/**
 * `onRetry` za `retryTransient` koji pokušaje piše u log. Prvi neuspeh ide kao upozorenje, ostali
 * tiše — pokušaji idu dok server ne odgovori.
 *
 * @param what šta se traži, za početak poruke — npr. `Loading products`.
 */
export function logRetry(logger: { warn(message: string): void; debug(message: string): void }, what: string) {
  return (error: HttpErrorResponse, attempt: number, delayMs: number) => {
    const message = `${what} failed (status ${error.status}) - retry ${attempt} in ${delayMs / 1000} s`;
    if (attempt === 1) {
      logger.warn(message);
    } else {
      logger.debug(message);
    }
  };
}
